import React, { useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import {
  LINK_IN_BIO_LIMITS,
  normalizeLinkInBioUrl,
  type LinkInBioBlock,
  type LinkInBioMedia,
  type LinkInBioPageType,
} from '@repo/lib';
import { getEnglishLinkInBioLabels } from '@repo/checkout';
import ImageUpload from '@dashboard/components/Formik/components/Image';
import PageFilterInput from '@dashboard/components/Form/components/PageFilterInput';
import HighlightField from '@dashboard/scenes/PageEdit/scenes/PageProduct/components/OrderBumps/HighlightField';
import { Button } from '@dashboard/components/ui/button';
import { Input } from '@dashboard/components/ui/input';
import { Label } from '@dashboard/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import { BLOCK_TYPE_LABELS, canShowAsCard, PAGE_TYPE_LABELS } from './blockLabels';
import {
  browserTimeZone,
  fromScheduleInput,
  timeZoneLabel,
  toScheduleInput,
} from './scheduleInput';

type Errors = Partial<Record<'title' | 'url' | 'page' | 'limit' | 'showUntil', string>>;

const englishLabels = getEnglishLinkInBioLabels();

const PAGE_PICKER: Record<
  LinkInBioPageType,
  { placeholder: string; error: string; description: string }
> = {
  checkout: {
    placeholder: 'Search your checkout pages',
    error: 'Choose a checkout page',
    description: 'Link to one of your checkout pages, like a membership or donations.',
  },
  event: {
    placeholder: 'Search your events',
    error: 'Choose an event',
    description: 'Show one event with its date and place. It drops off once the event has ended.',
  },
  form: {
    placeholder: 'Search your forms',
    error: 'Choose a form',
    description: 'Link to one of your forms, like a volunteer sign-up.',
  },
};

export function validateBlock(block: LinkInBioBlock, pageType?: LinkInBioPageType | null): Errors {
  const errors: Errors = {};
  if ((block.type === 'link' || block.type === 'heading') && !block.title?.trim()) {
    errors.title = 'Add a title';
  }
  if (block.type === 'link' && !normalizeLinkInBioUrl(block.url)) {
    errors.url = 'Enter a web address, like example.com';
  }
  if (block.type === 'page' && !block.page) {
    errors.page = pageType ? PAGE_PICKER[pageType].error : 'Choose a page';
  }
  if (
    block.type === 'events' &&
    !(
      Number(block.limit) >= LINK_IN_BIO_LIMITS.eventsMin &&
      Number(block.limit) <= LINK_IN_BIO_LIMITS.eventsMax
    )
  ) {
    errors.limit = `Choose a number from ${LINK_IN_BIO_LIMITS.eventsMin} to ${LINK_IN_BIO_LIMITS.eventsMax}`;
  }
  if (
    block.showFrom &&
    block.showUntil &&
    new Date(block.showUntil).getTime() <= new Date(block.showFrom).getTime()
  ) {
    errors.showUntil = 'Show until must be after show from';
  }
  return errors;
}

function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  id?: string;
  label: string;
  error?: string;
  hint?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && (
        <p id={id ? `${id}-error` : undefined} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

// The input keeps its own text: a datetime-local reports '' while half edited, and
// writing that back would wipe the parts already typed.
function ScheduleField({
  name,
  label,
  timeZone,
  error,
}: {
  name: 'showFrom' | 'showUntil';
  label: string;
  timeZone: string;
  error?: string;
}) {
  const { values, setFieldValue } = useFormikContext<LinkInBioBlock>();
  const [text, setText] = useState(() => toScheduleInput(values[name], timeZone));
  const id = `block-${name}`;

  const update = (next: string) => {
    setText(next);
    setFieldValue(name, fromScheduleInput(next, timeZone));
  };

  return (
    <Field id={id} label={label} error={error}>
      <div className="flex gap-2">
        <Input
          id={id}
          type="datetime-local"
          value={text}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : 'block-schedule-hint'}
          onChange={event => update(event.target.value)}
        />
        {text && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => update('')}
          >
            Clear
          </Button>
        )}
      </div>
    </Field>
  );
}

function ScheduleFields({ errors }: { errors: Errors }) {
  const { values } = useFormikContext<LinkInBioBlock>();
  const [isOpen, setIsOpen] = useState(!!(values.showFrom || values.showUntil));
  const timeZone = browserTimeZone();

  if (!isOpen) {
    return (
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto px-0"
        onClick={() => setIsOpen(true)}
      >
        Add a schedule
      </Button>
    );
  }

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">Schedule</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <ScheduleField name="showFrom" label="Show from" timeZone={timeZone} />
        <ScheduleField
          name="showUntil"
          label="Show until"
          timeZone={timeZone}
          error={errors.showUntil}
        />
      </div>
      <p id="block-schedule-hint" className="text-xs text-muted-foreground">
        Leave either empty to start now or keep showing. Times are in {timeZoneLabel(timeZone)}.
      </p>
    </fieldset>
  );
}

function TitleWithIcon({
  label,
  placeholder,
  error,
  hint,
}: {
  label: string;
  placeholder?: string;
  error?: string;
  hint?: string;
}) {
  return (
    <Field id="title" label={label} error={error} hint={hint}>
      <HighlightField
        name="title"
        iconName="icon"
        placeholder={placeholder}
        pickerLabel="Button icon"
        ariaLabel={undefined}
        maxLength={LINK_IN_BIO_LIMITS.blockTitle}
      />
    </Field>
  );
}

function BlockFields({
  errors,
  pageType,
}: {
  errors: Errors;
  pageType?: LinkInBioPageType | null;
}) {
  const { values: block, setFieldValue } = useFormikContext<LinkInBioBlock>();

  switch (block.type) {
    case 'page':
      return (
        <>
          <Field label={pageType ? PAGE_TYPE_LABELS[pageType] : 'Page'} error={errors.page}>
            <PageFilterInput
              label={undefined}
              name="page"
              placeholder={pageType ? PAGE_PICKER[pageType].placeholder : 'Search your pages'}
              pageTypes={pageType ? [pageType] : ['checkout', 'event', 'form']}
              initialValue={block.page}
              onChange={(value: string | null) => setFieldValue('page', value)}
            />
          </Field>
          <TitleWithIcon label="Title (optional)" hint="Leave empty to use the page title." />
        </>
      );
    case 'events':
      return (
        <Field
          id="block-limit"
          label="Number of events"
          error={errors.limit}
          hint="Lists every published upcoming event, soonest first, including ones you add later. Events without a date are not listed."
        >
          <Input
            id="block-limit"
            type="number"
            min={LINK_IN_BIO_LIMITS.eventsMin}
            max={LINK_IN_BIO_LIMITS.eventsMax}
            value={block.limit ?? ''}
            onChange={event =>
              setFieldValue('limit', event.target.value === '' ? null : Number(event.target.value))
            }
          />
        </Field>
      );
    case 'link':
      return (
        <>
          <TitleWithIcon
            label="Title"
            placeholder="Watch our services online"
            error={errors.title}
          />
          <Field id="block-url" label="URL" error={errors.url}>
            <Input
              id="block-url"
              value={block.url ?? ''}
              placeholder="example.com/live"
              maxLength={LINK_IN_BIO_LIMITS.url}
              aria-invalid={!!errors.url}
              onChange={event => setFieldValue('url', event.target.value)}
            />
          </Field>
          <Field
            label="Image (optional)"
            hint="Shown next to the link instead of the icon, or on top as a card."
          >
            <div className="h-16 w-16">
              <ImageUpload
                label={undefined}
                name="thumbnail"
                image={block.thumbnail}
                previewSize="logo"
                variant="minimal"
                crop={{ width: 512, height: 512, aspect: 1 }}
                onUpdate={(media: LinkInBioMedia | null) => setFieldValue('thumbnail', media)}
              />
            </div>
          </Field>
        </>
      );
    case 'heading':
      return (
        <Field id="block-title" label="Title" error={errors.title}>
          <Input
            id="block-title"
            value={block.title ?? ''}
            placeholder="Events"
            maxLength={LINK_IN_BIO_LIMITS.blockTitle}
            aria-invalid={!!errors.title}
            onChange={event => setFieldValue('title', event.target.value)}
          />
        </Field>
      );
    case 'email':
      return (
        <Field
          id="block-title"
          label="Title (optional)"
          hint="People who sign up are saved to your customers, with their email address."
        >
          <Input
            id="block-title"
            value={block.title ?? ''}
            placeholder={englishLabels.emailTitle}
            maxLength={LINK_IN_BIO_LIMITS.blockTitle}
            onChange={event => setFieldValue('title', event.target.value)}
          />
        </Field>
      );
    case 'portal':
      return (
        <TitleWithIcon
          label="Title (optional)"
          hint="Leave empty to show “Manage your purchases” in the visitor’s language."
        />
      );
    default:
      return null;
  }
}

function BlockForm({
  initial,
  isNew,
  pageType,
  onSave,
  onCancel,
}: {
  initial: LinkInBioBlock;
  isNew: boolean;
  pageType?: LinkInBioPageType | null;
  onSave: (block: LinkInBioBlock) => void;
  onCancel: () => void;
}) {
  const [showErrors, setShowErrors] = useState(false);

  return (
    <Formik<LinkInBioBlock> initialValues={initial} onSubmit={() => undefined}>
      {({ values: block, setFieldValue }) => {
        const errors = showErrors ? validateBlock(block, pageType) : {};
        const submit = () => {
          if (Object.keys(validateBlock(block, pageType)).length > 0) {
            setShowErrors(true);
            return;
          }
          onSave({
            ...block,
            title: block.title?.trim() || null,
            url: block.type === 'link' ? normalizeLinkInBioUrl(block.url) : null,
          });
        };

        return (
          <div
            className="space-y-4"
            onKeyDown={event => {
              if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') {
                event.preventDefault();
                submit();
              }
            }}
          >
            <BlockFields errors={errors} pageType={pageType} />
            {canShowAsCard(block) && (
              <Field label="Show as">
                <ToggleGroup
                  type="single"
                  variant="outline"
                  value={block.style}
                  aria-label="Show as"
                  onValueChange={value => value && setFieldValue('style', value)}
                >
                  <ToggleGroupItem value="button">Button</ToggleGroupItem>
                  <ToggleGroupItem value="card">Card with image</ToggleGroupItem>
                </ToggleGroup>
              </Field>
            )}
            <ScheduleFields errors={errors} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={submit}>
                {isNew ? 'Add block' : 'Save block'}
              </Button>
            </div>
          </div>
        );
      }}
    </Formik>
  );
}

const DESCRIPTIONS: Record<LinkInBioBlock['type'], string> = {
  page: 'Show one of your checkout pages, events or forms.',
  events: 'Your next events, kept up to date for you.',
  link: 'Link to anything else: your website, a video, a playlist.',
  heading: 'Start a section, like Events, Memberships or Give.',
  portal: 'A button to the customer portal, where buyers manage their purchases.',
};

export default function BlockEditor({
  block,
  isNew,
  pageType,
  onSave,
  onClose,
}: {
  block: LinkInBioBlock;
  isNew: boolean;
  pageType?: LinkInBioPageType | null;
  onSave: (block: LinkInBioBlock) => void;
  onClose: () => void;
}) {
  const kind = pageType ? PAGE_TYPE_LABELS[pageType] : BLOCK_TYPE_LABELS[block.type];
  const title = `${isNew ? 'Add' : 'Edit'} ${kind.toLowerCase()}`;
  const description = pageType ? PAGE_PICKER[pageType].description : DESCRIPTIONS[block.type];

  return (
    <div role="group" aria-label={title} className="space-y-4 rounded-lg border bg-muted/40 p-4">
      <div>
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <BlockForm
        initial={block}
        isNew={isNew}
        pageType={pageType}
        onSave={onSave}
        onCancel={onClose}
      />
    </div>
  );
}
